import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import i18n from "@/i18n";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary. Catches rendering errors anywhere in the
 * tree, logs them, and shows a friendly fallback with a "reload" button.
 *
 * Stays minimal on purpose — this is a final safety net, not a UX feature.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown) {
    // Surface to the console in dev; in prod it shows up in browser logs.
    // If we wire Sentry / LogRocket later, this is the hook point.
    // eslint-disable-next-line no-console
    console.error("[microsite] uncaught error", error, info);
  }

  handleReset = () => {
    this.setState({ error: null });
    window.location.assign("/");
  };

  render() {
    if (!this.state.error) return this.props.children;
    // Class component can't use the hook; read from the i18n instance directly.
    // The error screen is terminal, so reactive language switching isn't needed.
    const t = i18n.getFixedT(null, "common");
    return (
      <div className="min-h-screen bg-background text-foreground grid place-items-center p-8">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h1 className="font-display text-3xl tracking-normal">{t("errorBoundary.title")}</h1>
          <p className="mt-3 text-muted-foreground">
            {t("errorBoundary.body")}
          </p>
          <div className="mt-6 flex gap-3 justify-center">
            <Button onClick={this.handleReset}>{t("errorBoundary.reload")}</Button>
            <Button variant="outline" asChild>
              <a href="mailto:BBCKLRR@bizedge.com">{t("errorBoundary.contact")}</a>
            </Button>
          </div>
          {import.meta.env.DEV && (
            <pre className="mt-6 text-left text-xs text-muted-foreground overflow-auto max-h-48 bg-muted/40 p-3 rounded-md">
              {String(this.state.error.stack || this.state.error.message)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
